/**
 * TeamStrengthComparison - Paired dot plots for team metrics (Tufte-style).
 *
 * Pure CSS, no chart library. Each row shows two dots on a horizontal line.
 * Sport-agnostic: accepts array of {label, homeValue, awayValue, min, max, format}.
 *
 * @param {Array} metrics - [{label, homeValue, awayValue, min, max, format, lowerIsBetter}]
 * @param {Object} homeTeam - {name, abbreviation, logo}
 * @param {Object} awayTeam - {name, abbreviation, logo}
 */
import Card from '../../common/Card';

const defaultFormat = (v) => v != null ? v : '-';

const TeamStrengthComparison = ({
  metrics = [],
  homeTeam = {},
  awayTeam = {},
}) => {
  if (metrics.length === 0) return null;

  return (
    <Card className="p-3">
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">
        Strength Comparison
      </h3>

      {/* Team labels */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-1">
          {awayTeam.logo && (
            <img src={awayTeam.logo} alt="" className="w-4 h-4 object-contain" />
          )}
          <span className="text-[10px] font-semibold text-purple-400">
            {awayTeam.abbreviation || 'AWAY'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold text-accent-blue">
            {homeTeam.abbreviation || 'HOME'}
          </span>
          {homeTeam.logo && (
            <img src={homeTeam.logo} alt="" className="w-4 h-4 object-contain" />
          )}
        </div>
      </div>

      {/* Metric rows */}
      <div className="space-y-2">
        {metrics.map((metric, i) => {
          const { label, homeValue, awayValue, min, max, format, lowerIsBetter } = metric;
          const formatter = format || defaultFormat;

          // Skip if both values are null
          if (homeValue == null && awayValue == null) return null;

          // Calculate positions (0-100%)
          const range = (max || 100) - (min || 0);
          const minVal = min || 0;

          const homePos = homeValue != null
            ? Math.max(0, Math.min(100, ((homeValue - minVal) / range) * 100))
            : 50;
          const awayPos = awayValue != null
            ? Math.max(0, Math.min(100, ((awayValue - minVal) / range) * 100))
            : 50;

          // For rank metrics (lower is better), invert position
          const homeDisplay = lowerIsBetter ? (100 - homePos) : homePos;
          const awayDisplay = lowerIsBetter ? (100 - awayPos) : awayPos;

          // Determine which team is better for this metric
          let homeBetter = false;
          let awayBetter = false;
          if (homeValue != null && awayValue != null) {
            if (lowerIsBetter) {
              homeBetter = homeValue < awayValue;
              awayBetter = awayValue < homeValue;
            } else {
              homeBetter = homeValue > awayValue;
              awayBetter = awayValue > homeValue;
            }
          }

          return (
            <div key={i} className="flex items-center gap-2">
              {/* Away value */}
              <span className={`font-mono text-[10px] w-10 text-right flex-shrink-0 ${
                awayBetter ? 'text-purple-400 font-semibold' : 'text-text-secondary'
              }`}>
                {formatter(awayValue)}
              </span>

              {/* Dot plot track */}
              <div className="flex-1 relative h-3">
                {/* Track line */}
                <div className="absolute top-1/2 left-0 right-0 h-px bg-border/40"></div>

                {/* Away dot */}
                {awayValue != null && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-purple-400 border border-purple-300/50"
                    style={{ left: `${awayDisplay}%`, marginLeft: '-4px' }}
                  />
                )}

                {/* Home dot */}
                {homeValue != null && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-accent-blue border border-accent-blue/50"
                    style={{ left: `${homeDisplay}%`, marginLeft: '-4px' }}
                  />
                )}
              </div>

              {/* Home value */}
              <span className={`font-mono text-[10px] w-10 text-left flex-shrink-0 ${
                homeBetter ? 'text-accent-blue font-semibold' : 'text-text-secondary'
              }`}>
                {formatter(homeValue)}
              </span>

              {/* Label */}
              <span className="text-[9px] text-text-secondary w-16 text-right flex-shrink-0">
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default TeamStrengthComparison;
